/**
 * Costs a departure from what has been sold against it.
 *
 * Each package option is priced by how many travellers took *that option*, not
 * by the size of the whole departure: a supplier quotes a scale per option, so
 * an option carrying four travellers is bought at its four-traveller price even
 * where another option on the same departure carries more. VIP is the exception:
 * it is an upgrade, so its travellers are also bought the Regular tour.
 */
import { DepartureInventory, InventoryEntry } from '~/types/tours/inventory';

import { toProperCase } from '~/utils/stringUtils';

/**
 * The part of a sliding scale row this module needs. A whole-tour scale and a
 * per-departure one differ only in that the latter names a departure, so both
 * satisfy this and both are costed the same way.
 */
export interface ScalePricingItem {
  season: string;
  tourConfiguration: string;
  packageConfiguration: string;
  currency: string;
  singleSupplement: number;
  scale: Array<number>;
  /** Group total is retained so rates remain exact beyond the displayed scale. */
  groupPrice?: number;
}

interface FixedPricingItem {
  season: string;
  tourConfiguration: string;
  packageConfiguration: string;
  currency: string;
  price: number;
}

/** Represent a fixed per-traveller rate across every observed inventory level. */
export function fixedPricingAsScale(items: Array<FixedPricingItem>, paxCapacity: number): Array<ScalePricingItem> {
  const scaleLength = Math.max(1, paxCapacity);

  return items.map((item) => ({
    season: item.season,
    tourConfiguration: item.tourConfiguration,
    packageConfiguration: item.packageConfiguration,
    currency: item.currency,
    singleSupplement: 0,
    scale: Array.from({ length: scaleLength }, () => item.price),
  }));
}

/** Display one through 24 pax by default; calculation always uses the actual allocation size. */
export function fixedGroupPricingAsScale<T extends FixedPricingItem>(
  items: Array<T>,
  paxCapacity = 24,
): Array<T & ScalePricingItem> {
  return items.map((item) => ({
    ...item,
    groupPrice: item.price,
    singleSupplement: 0,
    scale: Array.from({ length: Math.max(24, paxCapacity) }, (_value, index) => item.price / (index + 1)),
  }));
}

export function pricingRateAtPax(pricing: ScalePricingItem, pax: number): ScaleRate {
  if (pricing.groupPrice === undefined || pax < 1 || !pricing.scale.length) {
    return rateAtPax(pricing.scale, pax);
  }

  // Aggregated scales can include ordinary per-pax costs alongside the group share.
  const step = Math.min(pax, pricing.scale.length);
  const perPaxCost = pricing.scale[step - 1] - pricing.groupPrice / step;
  return { unitPrice: perPaxCost + pricing.groupPrice / pax, pricedAtPax: pax, isBeyondScale: false };
}

/**
 * The part of a shared departure's ledger the tour sold itself. An LTLE built on the tour
 * shares the departure but has its own cost prices, so a tour is banded and costed only on
 * its own travellers. Rows with no tourOfferId predate it being reported, so count as the tour's.
 */
export function ownInventory(
  inventory: DepartureInventory | undefined,
  tourOfferId: string | undefined,
): DepartureInventory | undefined {
  if (!inventory || !tourOfferId) {
    return inventory;
  }

  const entries = inventory.entries.filter((entry) => !entry.tourOfferId || entry.tourOfferId === tourOfferId);
  const counts = new Map<string, number>();
  entries.forEach((entry) =>
    counts.set(entry.packageConfigurationId, (counts.get(entry.packageConfigurationId) ?? 0) + entry.units),
  );

  return {
    ...inventory,
    entries,
    units: entries.reduce((total, entry) => total + entry.units, 0),
    configurations: inventory.configurations.map((configuration) => ({
      ...configuration,
      units: counts.get(configuration.packageConfigurationId) ?? 0,
    })),
  };
}

/** One tour's share of a departure's travellers: the tour's own, or an LTLE built on it. */
export interface OfferShare {
  /** Null for the tour's own travellers. */
  tourOfferId: string | null;
  isOwn: boolean;
  /** "This tour", or "LTLE 1", "LTLE 2" in tour offer order. */
  label: string;
  /** An LTLE's place in the theme's LTLE colours; the tour's own has none and takes primary. */
  colourIndex?: number;
  units: number;
  /** Each keyed however the caller keys its configurations. */
  byConfiguration: Record<string, number>;
  singlesByConfiguration: Record<string, number>;
  bookingsByConfiguration: Record<string, number>;
}

/**
 * Splits a departure's travellers by the tour that sold them: the tour's own first, then each
 * LTLE built on it in tour offer order, so an LTLE keeps its colour wherever it is drawn.
 */
export function sharesByOffer(
  entries: Array<InventoryEntry>,
  tourOfferId: string | undefined,
  keyOf: (entry: InventoryEntry) => string,
): Array<OfferShare> {
  const blank = (id: string | null): OfferShare => ({
    tourOfferId: id,
    isOwn: id === null,
    label: 'This tour',
    units: 0,
    byConfiguration: {},
    singlesByConfiguration: {},
    bookingsByConfiguration: {},
  });
  const own = blank(null);
  const linked = new Map<string, OfferShare>();
  // Net travellers per booking, so a booking released in full no longer counts.
  const perBooking = new Map<OfferShare, Map<string, Map<string, number>>>();

  entries.forEach((entry) => {
    const linkedId = tourOfferId && entry.tourOfferId && entry.tourOfferId !== tourOfferId ? entry.tourOfferId : null;
    const share = linkedId ? (linked.get(linkedId) ?? blank(linkedId)) : own;
    if (linkedId) {
      linked.set(linkedId, share);
    }

    const key = keyOf(entry);
    share.units += entry.units;
    share.byConfiguration[key] = (share.byConfiguration[key] ?? 0) + entry.units;
    if (isSingleRoom(entry.passenger.roomType)) {
      share.singlesByConfiguration[key] = (share.singlesByConfiguration[key] ?? 0) + entry.units;
    }

    const booking = entry.reservation.bookingReference ?? entry.orderId;
    if (booking) {
      const byKey = perBooking.get(share) ?? new Map<string, Map<string, number>>();
      const bookings = byKey.get(key) ?? new Map<string, number>();
      bookings.set(booking, (bookings.get(booking) ?? 0) + entry.units);
      byKey.set(key, bookings);
      perBooking.set(share, byKey);
    }
  });

  perBooking.forEach((byKey, share) =>
    byKey.forEach((bookings, key) => {
      share.bookingsByConfiguration[key] = Array.from(bookings.values()).filter((units) => units > 0).length;
    }),
  );

  const ltles = Array.from(linked.values())
    .sort((a, b) => (a.tourOfferId ?? '').localeCompare(b.tourOfferId ?? ''))
    .map((share, index) => ({
      ...share,
      label: `LTLE ${index + 1}`,
      colourIndex: index,
    }));

  return [own, ...ltles];
}

/** Inventory and pricing spell the same option with different casing. */
export function configurationKey(tourConfiguration: string, packageConfiguration: string): string {
  return `${tourConfiguration}::${packageConfiguration}`.toLowerCase();
}

export function configurationLabel(tourConfiguration: string, packageConfiguration: string): string {
  return `${toProperCase(tourConfiguration)}-${toProperCase(packageConfiguration)}`;
}

/** Inventory and pricing spell the same option with different casing. */
export function configurationLabelKey(label: string): string {
  return label.toLowerCase();
}

/** The room type enum spells several singles - `single`, `single_vip` - so match the prefix. */
export function isSingleRoom(roomType: string | null): boolean {
  return !!roomType && roomType.toLowerCase().startsWith('single');
}

const UPGRADE_PACKAGE = 'vip';
const BASE_PACKAGE = 'regular';

/** Travellers and the single rooms among them. */
export interface TravellerCount {
  units: number;
  singles: number;
}

interface OptionCount extends TravellerCount {
  tourConfiguration: string;
  packageConfiguration: string;
}

const NO_TRAVELLERS: TravellerCount = { units: 0, singles: 0 };

/** The Regular option a VIP upgrade is bought on top of; null for any other option. */
function baseKeyOf(tourConfiguration: string, packageConfiguration: string): string | null {
  return packageConfiguration.toLowerCase() === UPGRADE_PACKAGE
    ? configurationKey(tourConfiguration, BASE_PACKAGE)
    : null;
}

/**
 * VIP is priced as an upgrade on the Regular tour, so each VIP traveller is also bought the
 * Regular tour: counted towards its band and charged its single supplement. Keyed by the
 * Regular option's `configurationKey`; a VIP with no Regular price stays standalone.
 */
export function upgradeTravellersByBase(
  counts: Iterable<OptionCount>,
  byConfiguration: Map<string, ScalePricingItem>,
): Map<string, TravellerCount> {
  const byBase = new Map<string, TravellerCount>();

  for (const { tourConfiguration, packageConfiguration, units, singles } of counts) {
    const baseKey = baseKeyOf(tourConfiguration, packageConfiguration);

    if (!baseKey || !byConfiguration.has(baseKey)) {
      continue;
    }

    const existing = byBase.get(baseKey) ?? NO_TRAVELLERS;
    byBase.set(baseKey, { units: existing.units + units, singles: existing.singles + singles });
  }

  return byBase;
}

export interface ScaleRate {
  unitPrice: number | null;
  /** The count the price was read at, once clamped to the scale. */
  pricedAtPax: number | null;
  isBeyondScale: boolean;
}

/**
 * An option carrying more travellers than the supplier priced for is costed at
 * their largest quoted step and flagged, rather than having a price invented
 * past the end of the scale.
 */
export function rateAtPax(scale: Array<number>, pax: number): ScaleRate {
  if (!scale.length || pax < 1) {
    return { unitPrice: null, pricedAtPax: null, isBeyondScale: false };
  }

  if (pax <= scale.length) {
    return { unitPrice: scale[pax - 1], pricedAtPax: pax, isBeyondScale: false };
  }

  return { unitPrice: scale[scale.length - 1], pricedAtPax: scale.length, isBeyondScale: true };
}

/** Every currency this service quotes, so one can be chosen deliberately. */
export function availableCurrencies(items: Array<ScalePricingItem>): Array<string> {
  return Array.from(new Set(items.map((item) => item.currency))).sort();
}

interface PricingIndex {
  byConfiguration: Map<string, ScalePricingItem>;
  /** The season the prices were read from, null where it could not be resolved. */
  seasonUsed: string | null;
  /** Every season this service prices, for explaining an unresolved one. */
  seasonsPriced: Array<string>;
}

/**
 * The scale each option is priced by, for one season and one currency.
 *
 * Both narrowings matter. Costing across currencies would add dong to dollars;
 * and where the departure's season cannot be matched, nothing is returned
 * rather than guessed - a confident wrong number is worse than none on a tour
 * whose low and high scales differ by millions.
 */
export function indexPricing(
  items: Array<ScalePricingItem>,
  season: string | null,
  currency: string | null,
): PricingIndex {
  const inCurrency = currency === null ? items : items.filter((item) => item.currency === currency);
  const seasonsPriced = Array.from(new Set(inCurrency.map((item) => item.season))).sort();

  const matched = season ? seasonsPriced.find((priced) => priced.toLowerCase() === season.toLowerCase()) : undefined;

  // One priced season is no choice at all, so an unnamed departure season is
  // safe there - the ambiguity this guards against cannot arise.
  const seasonUsed = matched ?? (!season && seasonsPriced.length === 1 ? seasonsPriced[0] : null);

  const byConfiguration = new Map<string, ScalePricingItem>();

  inCurrency
    .filter((item) => item.season === seasonUsed)
    .forEach((item) => {
      byConfiguration.set(configurationKey(item.tourConfiguration, item.packageConfiguration), item);
    });

  return { byConfiguration, seasonUsed, seasonsPriced };
}

export interface ScaleAnnotation {
  departureName: string;
  season: string | null;
  /** Keyed by `configurationLabelKey`; read it back the same way. */
  soldByConfiguration: Record<string, number>;
  /** Single-room travellers, keyed by the configuration carrying their supplement. */
  singlesByConfiguration: Record<string, number>;
  /** VIP travellers each Regular row is also bought for, so it is read at their combined count. */
  upgradesByConfiguration: Record<string, TravellerCount>;
  /** Everyone on the shared departure, by the tour that sold them; empty without the tour's offer. */
  shares: Array<OfferShare>;
}

/**
 * Where a departure sits on the service's price table, for marking it there
 * rather than reprinting the whole scale beneath it. An option at zero has no
 * position to mark.
 */
export function buildScaleAnnotation(
  inventory: DepartureInventory | undefined,
  departureName: string,
  pricing: Array<ScalePricingItem> = [],
  tourOfferId?: string,
): ScaleAnnotation | undefined {
  if (!inventory) {
    return undefined;
  }

  // The band is the tour's own; the split below still names everyone on the departure.
  const own = ownInventory(inventory, tourOfferId) ?? inventory;

  const soldByConfiguration: Record<string, number> = {};
  const singlesByConfiguration: Record<string, number> = {};

  own.configurations
    .filter((configuration) => configuration.units > 0)
    .forEach((configuration) => {
      const label = configurationLabel(configuration.tourConfiguration, configuration.packageConfiguration);

      soldByConfiguration[configurationLabelKey(label)] = configuration.units;
    });

  own.entries
    .filter((entry) => isSingleRoom(entry.passenger.roomType))
    .forEach((entry) => {
      const configuration = own.configurations.find(
        ({ packageConfigurationId }) => packageConfigurationId === entry.packageConfigurationId,
      );

      if (!configuration) {
        return;
      }

      const label = configurationLabel(configuration.tourConfiguration, configuration.packageConfiguration);
      const key = configurationLabelKey(label);

      singlesByConfiguration[key] = (singlesByConfiguration[key] ?? 0) + entry.units;
    });

  const pricedKeys = new Map(
    pricing.map((item) => [configurationKey(item.tourConfiguration, item.packageConfiguration), item]),
  );
  const upgradesByConfiguration: Record<string, TravellerCount> = {};
  upgradeTravellersByBase(
    own.configurations.map((configuration) => {
      const key = configurationLabelKey(
        configurationLabel(configuration.tourConfiguration, configuration.packageConfiguration),
      );
      return { ...configuration, singles: singlesByConfiguration[key] ?? 0 };
    }),
    pricedKeys,
  ).forEach((travellers, key) => {
    const base = pricedKeys.get(key);
    if (base && travellers.units > 0) {
      upgradesByConfiguration[
        configurationLabelKey(configurationLabel(base.tourConfiguration, base.packageConfiguration))
      ] = travellers;
    }
  });

  // Prefer the inventory season; retain the single-season fallback for older APIs.
  const seasons = Array.from(new Set(pricing.map((item) => item.season.toLowerCase())));
  const season = inventory.season ?? (seasons.length === 1 ? seasons[0] : null);

  const shares = tourOfferId
    ? sharesByOffer(inventory.entries, tourOfferId, (entry) =>
        configurationLabelKey(configurationLabel(entry.tourConfiguration, entry.packageConfiguration)),
      )
    : [];

  return { departureName, season, soldByConfiguration, singlesByConfiguration, upgradesByConfiguration, shares };
}

/**
 * A price row describes the departure only when their seasons agree.
 *
 * An unknown season marks nothing rather than everything: marking each season's
 * row would put the departure in two places at once, and a missing mark is safe
 * where a wrong one is not.
 */
export function matchesAnnotationSeason(
  rowSeason: string | null | undefined,
  annotationSeason: string | null | undefined,
): boolean {
  if (!annotationSeason || !rowSeason) {
    return false;
  }

  return rowSeason.toLowerCase() === annotationSeason.toLowerCase();
}

/** What one more traveller on an option would add, once repricing is included. */
export interface NextTraveller {
  pax: number;
  unitPrice: number;
  addedCost: number;
}

/** What one package option contributes to the departure's cost. */
export interface ConfigurationCost {
  packageConfigurationId: string;
  label: string;
  season: string | null;
  currency: string | null;
  units: number;
  singles: number;
  /** VIP travellers this Regular option is also bought for; zero on every other option. */
  upgradeTravellers: TravellerCount;
  /** A VIP option priced as an upgrade on its Regular option, rather than on its own. */
  isUpgrade: boolean;
  unitPrice: number | null;
  singleSupplement: number;
  /** The single-room supplement contribution for this configuration. */
  singleSupplementCost: number | null;
  /** Travellers at the option's price, plus the supplement on every single room. */
  cost: number | null;
  /** True where inventory was sold on an option carrying no cost price. */
  isUnpriced: boolean;
  pricedAtPax: number | null;
  nextTraveller: NextTraveller | null;
  isBeyondScale: boolean;
  /** The prices this option is sold at, null where it carries no cost price. */
  scale: Array<number> | null;
}

export interface DepartureCostSummary {
  /** Net travellers on the departure. */
  units: number;
  /** Net travellers occupying single rooms. */
  singles: number;
  currency: string | null;
  /** Null once options sit at different counts, where no one figure describes the departure. */
  pricedAtPax: number | null;
  isBeyondScale: boolean;
  configurations: Array<ConfigurationCost>;
  /** Null until something is sold, or where nothing sold is priced. */
  totalCost: number | null;
  /** The part of totalCost contributed by single-room supplements. */
  totalSingleSupplementCost: number | null;
  seasonUsed: string | null;
  seasonsPriced: Array<string>;
  /** The departure's own season, echoed back for explaining an unresolved one. */
  departureSeason: string | null;
}

function countSinglesByConfiguration(entries: Array<InventoryEntry>): Map<string, number> {
  const singles = new Map<string, number>();

  entries
    .filter((entry) => isSingleRoom(entry.passenger.roomType))
    .forEach((entry) => {
      singles.set(entry.packageConfigurationId, (singles.get(entry.packageConfigurationId) ?? 0) + entry.units);
    });

  return singles;
}

/** One option's cost at a given traveller count, supplements included. */
function costOptionAt(pricing: ScalePricingItem, units: number, singles: number): number | null {
  const { unitPrice } = pricingRateAtPax(pricing, units);

  if (unitPrice === null) {
    return null;
  }
  const step = Math.min(units, pricing.scale.length);
  const total =
    pricing.groupPrice === undefined
      ? units * unitPrice
      : pricing.groupPrice + units * (pricing.scale[step - 1] - pricing.groupPrice / step);
  return total + singles * pricing.singleSupplement;
}

/**
 * Reaching an option's next priced size reprices the travellers already on it,
 * so the whole increment is quoted - a traveller can add less than their own
 * price, occasionally less than nothing.
 */
function nextTravellerOn(
  pricing: ScalePricingItem,
  units: number,
  singles: number,
  base?: { pricing: ScalePricingItem; travellers: TravellerCount },
): NextTraveller | null {
  const pax = units + 1;

  if (pricing.groupPrice === undefined && pax > pricing.scale.length) {
    return null;
  }

  const { unitPrice } = pricingRateAtPax(pricing, pax);
  const after = costOptionAt(pricing, pax, singles);

  if (unitPrice === null || after === null) {
    return null;
  }

  // One more VIP is also one more traveller on the Regular tour beneath it.
  const baseAddedCost = base
    ? (costOptionAt(base.pricing, base.travellers.units + 1, base.travellers.singles) ?? 0) -
      (costOptionAt(base.pricing, base.travellers.units, base.travellers.singles) ?? 0)
    : 0;

  return { pax, unitPrice, addedCost: after - (costOptionAt(pricing, units, singles) ?? 0) + baseAddedCost };
}

export function summariseDepartureCost(
  inventory: DepartureInventory | undefined,
  items: Array<ScalePricingItem>,
  options?: { currency?: string | null },
): DepartureCostSummary {
  const departureSeason = inventory?.season ?? null;
  const currency = options?.currency ?? availableCurrencies(items)[0] ?? null;
  const { byConfiguration, seasonUsed, seasonsPriced } = indexPricing(items, departureSeason, currency);

  const singlesByConfiguration = countSinglesByConfiguration(inventory?.entries ?? []);
  const sold = (inventory?.configurations ?? []).map((configuration) => ({
    ...configuration,
    key: configurationKey(configuration.tourConfiguration, configuration.packageConfiguration),
    singles: singlesByConfiguration.get(configuration.packageConfigurationId) ?? 0,
  }));
  const upgradeTravellers = upgradeTravellersByBase(sold, byConfiguration);

  // A VIP-only departure still buys the Regular tour, though nobody sold that option.
  upgradeTravellers.forEach((travellers, key) => {
    const base = byConfiguration.get(key);
    if (base && travellers.units > 0 && !sold.some((option) => option.key === key)) {
      sold.push({
        packageConfigurationId: key,
        key,
        tourConfiguration: base.tourConfiguration,
        packageConfiguration: base.packageConfiguration,
        units: 0,
        singles: 0,
      });
    }
  });

  // What each option is bought for: its own travellers, plus the VIP travellers on a Regular one.
  const bought = new Map<string, TravellerCount>(
    sold.map((option) => {
      const upgrade = upgradeTravellers.get(option.key) ?? NO_TRAVELLERS;
      return [option.key, { units: option.units + upgrade.units, singles: option.singles + upgrade.singles }];
    }),
  );

  const configurations: Array<ConfigurationCost> = sold.map((configuration) => {
    const { packageConfigurationId, key, tourConfiguration, packageConfiguration, units, singles } = configuration;
    const upgrade = upgradeTravellers.get(key) ?? NO_TRAVELLERS;
    const pricing = byConfiguration.get(key);
    const label = configurationLabel(tourConfiguration, packageConfiguration);

    if (!pricing) {
      return {
        packageConfigurationId,
        label,
        season: null,
        currency: null,
        units,
        singles,
        upgradeTravellers: upgrade,
        isUpgrade: false,
        unitPrice: null,
        singleSupplement: 0,
        singleSupplementCost: null,
        cost: null,
        // An option nobody booked and nobody priced is not a problem to report.
        isUnpriced: units > 0,
        scale: null,
        pricedAtPax: null,
        nextTraveller: null,
        isBeyondScale: false,
      };
    }

    const priced = bought.get(key) ?? NO_TRAVELLERS;
    const { unitPrice, pricedAtPax, isBeyondScale } = pricingRateAtPax(pricing, priced.units);
    const baseKey = baseKeyOf(tourConfiguration, packageConfiguration);
    const basePricing = baseKey ? byConfiguration.get(baseKey) : undefined;

    return {
      packageConfigurationId,
      label,
      season: pricing.season,
      currency: pricing.currency,
      units,
      singles,
      upgradeTravellers: upgrade,
      isUpgrade: !!basePricing,
      unitPrice,
      singleSupplement: pricing.singleSupplement,
      singleSupplementCost: priced.singles * pricing.singleSupplement,
      cost: costOptionAt(pricing, priced.units, priced.singles),
      isUnpriced: false,
      scale: pricing.scale,
      pricedAtPax,
      nextTraveller: nextTravellerOn(
        pricing,
        priced.units,
        priced.singles,
        basePricing && { pricing: basePricing, travellers: bought.get(baseKey) ?? NO_TRAVELLERS },
      ),
      isBeyondScale,
    };
  });

  const costed = configurations.filter((configuration) => configuration.cost !== null);

  // Each option is read at its own count, so a departure-wide figure exists
  // only while they happen to agree.
  const pricedCounts = new Set(costed.map((configuration) => configuration.pricedAtPax));

  return {
    units: inventory?.units ?? 0,
    singles: configurations.reduce((total, configuration) => total + configuration.singles, 0),
    currency,
    pricedAtPax: pricedCounts.size === 1 ? costed[0].pricedAtPax : null,
    isBeyondScale: configurations.some((configuration) => configuration.isBeyondScale),
    configurations,
    totalCost: costed.length ? costed.reduce((total, configuration) => total + configuration.cost, 0) : null,
    totalSingleSupplementCost: costed.length
      ? costed.reduce((total, configuration) => total + (configuration.singleSupplementCost ?? 0), 0)
      : null,
    seasonUsed,
    seasonsPriced,
    departureSeason,
  };
}

/** One movement on the departure, with where it left the departure standing. */
export interface TimelinePoint {
  entry: InventoryEntry;
  occurredAt: string;
  /** Net travellers on the departure after this movement. */
  unitsAfter: number;
  /**
   * The price of the option *this* traveller took, at its count after the
   * movement. Per option, so a VIP traveller is never shown the standard price.
   */
  entryUnitPrice: number | null;
  /** What the departure owed the supplier after this movement. */
  totalCost: number | null;
  /**
   * Change in what is owed against the movement before it, null on the first.
   * Folds in both the traveller and the repricing of everyone already booked,
   * so it can exceed that traveller's own price.
   */
  totalCostDelta: number | null;
}

/**
 * Replays the ledger so a departure's build-up can be read as it happened.
 *
 * Cost is rebuilt from the running per-option counts at every step rather than
 * carried forward, because reaching a new size reprices travellers who booked
 * long before it.
 */
export function buildTimeline(
  inventory: DepartureInventory | undefined,
  items: Array<ScalePricingItem>,
  currency?: string | null,
): Array<TimelinePoint> {
  const entries = (inventory?.entries ?? []).slice().sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

  const { byConfiguration } = indexPricing(
    items,
    inventory?.season ?? null,
    currency ?? availableCurrencies(items)[0] ?? null,
  );

  const running = new Map<string, OptionCount>();

  let unitsAfter = 0;
  let previousTotalCost: number | null = null;

  return entries.map((entry) => {
    const key = configurationKey(entry.tourConfiguration, entry.packageConfiguration);
    const previous = running.get(key) ?? {
      tourConfiguration: entry.tourConfiguration,
      packageConfiguration: entry.packageConfiguration,
      ...NO_TRAVELLERS,
    };

    unitsAfter += entry.units;
    running.set(key, {
      ...previous,
      units: previous.units + entry.units,
      singles: previous.singles + (isSingleRoom(entry.passenger.roomType) ? entry.units : 0),
    });

    const upgrades = upgradeTravellersByBase(running.values(), byConfiguration);
    const boughtFor = (optionKey: string): TravellerCount => {
      const own = running.get(optionKey) ?? NO_TRAVELLERS;
      const upgrade = upgrades.get(optionKey) ?? NO_TRAVELLERS;
      return { units: own.units + upgrade.units, singles: own.singles + upgrade.singles };
    };

    let totalCost: number | null = null;

    for (const optionKey of new Set([...running.keys(), ...upgrades.keys()])) {
      const pricing = byConfiguration.get(optionKey);
      const { units, singles } = boughtFor(optionKey);
      const cost = pricing ? costOptionAt(pricing, units, singles) : null;

      if (cost !== null) {
        totalCost = (totalCost ?? 0) + cost;
      }
    }

    const ownPricing = byConfiguration.get(key);
    const entryUnitPrice = ownPricing ? pricingRateAtPax(ownPricing, boughtFor(key).units).unitPrice : null;
    const totalCostDelta = previousTotalCost === null || totalCost === null ? null : totalCost - previousTotalCost;

    previousTotalCost = totalCost;

    return { entry, occurredAt: entry.occurredAt, unitsAfter, entryUnitPrice, totalCost, totalCostDelta };
  });
}
